import { PREFERRED_ACTIVITY_OPTIONS } from "@/components/assessment/assessmentData";
import { ACTIVITY_PROGRESSION_REPOSITORY } from "@/components/assessment/activityProgressionRepository";
import {
  BEDTIME_ROUTINES,
  ENVIRONMENT_OUTPUTS,
  FALLBACK_LABELS,
  FALLBACK_LEVELS,
  MINIMUM_INPUTS,
  MORNING_ROUTINES,
  SOMATIC_OPTIONS,
  STATE_KNOWLEDGE,
} from "./knowledgeBank";

const DAY_CODES = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
const DAY_LABELS = {
  SU: "Sun",
  MO: "Mon",
  TU: "Tue",
  WE: "Wed",
  TH: "Thu",
  FR: "Fri",
  SA: "Sat",
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const unique = (values) => [...new Set((values || []).filter(Boolean))];

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const activityLabel = (key, fallback = "Familiar activity") =>
  PREFERRED_ACTIVITY_OPTIONS.find((option) => option.key === key)?.label || fallback;

const firstSelectedActivity = (schedule = {}) =>
  (schedule.preferredActivities || []).find((key) => key && key !== "none") || null;

const normalizeEnvironment = (value) =>
  ["outside", "gym", "inside", "limited"].includes(value) ? value : "inside";

const normalizeFallbackLevel = (value) =>
  FALLBACK_LEVELS.includes(value) ? value : "primary";

const normalizeScheduledActivities = (schedule = {}) => {
  const current = Array.isArray(schedule.scheduledActivities)
    ? schedule.scheduledActivities.filter(
        (activity) => activity?.activity && (activity.days || []).length,
      )
    : [];
  if (current.length) return current;

  if (
    schedule.scheduledParticipation === "scheduled" &&
    schedule.scheduledActivity &&
    (schedule.scheduledActivityDays || []).length
  ) {
    return [
      {
        id: "scheduled-activity-1",
        activity: schedule.scheduledActivity,
        days: schedule.scheduledActivityDays,
        time: schedule.scheduledActivityTime || "18:00",
        duration: schedule.scheduledActivityDuration || "60",
        effort: schedule.scheduledActivityEffort || "moderate",
        warmupRecovery: schedule.warmupRecovery || "neither",
      },
    ];
  }

  return [];
};

const inferredConditioning = (answers = {}) => {
  const explicit =
    answers.current?.conditioningLevel || answers.schedule?.conditioningLevel;
  if (["beginner", "general", "advanced", "athlete"].includes(explicit)) {
    return explicit;
  }

  const capacity = answers.capacity || {};
  const values = [capacity.activity, capacity.confidence, capacity.tolerance]
    .map(Number)
    .filter(Number.isFinite);
  const average = values.length
    ? values.reduce((sum, value) => sum + value, 0) / values.length
    : 3;
  if (average <= 2) return "beginner";
  if (average >= 4.5) return "athlete";
  if (average >= 3.7) return "advanced";
  return "general";
};

const conditioningLabel = (value) =>
  ({ beginner: "Beginner", general: "General", advanced: "Advanced", athlete: "Athlete" })[
    value
  ] || "General";

const timeFromAvailability = (key) =>
  ({ none: 0, "2-5": 3, "5-10": 7, "10-20": 15, "20-30": 25, "30-plus": 30 })[
    key
  ] || 0;

const mainTimeSlot = (schedule = {}) => {
  const stressWindow = schedule.stressWindow;
  if (["before-day", "morning"].includes(stressWindow)) return "start";
  if (stressWindow === "midday") return "middle";
  return "end";
};

const targetOutputClass = (state, eligibility) => {
  if (eligibility.level === "pause") return "somatic";
  if (eligibility.level === "modify") return "low-activation";
  if (eligibility.level === "moderate" && state.defaultOutput === "high-output") {
    return "moderate-output";
  }
  return state.defaultOutput;
};

const relativeIntensity = ({ outputClass, conditioning, eligibility }) => {
  if (eligibility.level === "pause") return "No exercise dose";
  if (eligibility.level === "modify") return "Light and modified";
  if (eligibility.level === "moderate") return "Light to moderate";
  if (outputClass === "high-output") {
    return conditioning === "beginner"
      ? "High relative to beginner ability, using short controlled efforts"
      : "High but controlled, relative to current ability";
  }
  if (outputClass === "moderate-output") return "Moderate and controlled";
  if (outputClass === "low-activation") return "Light and accessible";
  return "Matched to current ability";
};

const recoveryDurationFor = (outputClass, minutesAvailable) => {
  if (["somatic", "minimum"].includes(outputClass)) return 0;
  const available = clamp(Number(minutesAvailable) || 7, 1, 30);
  const target = outputClass === "high-output" ? 8 : 4;
  return Math.min(target, Math.max(0, available - 1));
};

const durationFor = ({
  outputClass,
  minutesAvailable,
  conditioning,
  fallbackLevel,
  recoveryDuration,
}) => {
  const available = clamp(Number(minutesAvailable) || 7, 1, 30);
  const usable = Math.max(1, available - Number(recoveryDuration || 0));
  if (fallbackLevel === "minimum") return Math.min(2, usable);
  if (fallbackLevel === "somatic") return Math.min(7, usable);
  if (outputClass === "high-output") {
    const target = conditioning === "beginner" ? 10 : 20;
    return Math.max(1, Math.min(target, usable));
  }
  if (outputClass === "moderate-output") return Math.max(1, Math.min(20, usable));
  if (outputClass === "low-activation") return Math.max(1, Math.min(12, usable));
  return Math.max(1, Math.min(20, usable));
};

export function interpretReportedState(current = {}) {
  const suppliedStates = Array.isArray(current.states) && current.states.length
    ? current.states
    : [current.primaryState, current.secondaryState];
  const stateKeys = unique(suppliedStates);
  const primaryKey = stateKeys[0] || "calm-steady";
  const knowledge = STATE_KNOWLEDGE[primaryKey] || STATE_KNOWLEDGE["calm-steady"];
  return {
    primaryKey,
    secondaryKey: stateKeys[1] || null,
    labels: stateKeys.map((key) => STATE_KNOWLEDGE[key]?.label || key),
    intensity: clamp(Number(current.intensity) || 3, 1, 5),
    energy: clamp(Number(current.energy) || 3, 1, 5),
    urge: current.urge || "none",
    ...knowledge,
  };
}

export function determineExerciseEligibility({ answers = {}, current = {} }) {
  const readiness = current.physicalReadiness || "light-moderate";
  const conditioning = inferredConditioning({ ...answers, current });

  if (readiness === "pause") {
    return {
      level: "pause",
      title: "Exercise should wait",
      reason:
        "The physical-readiness response indicates that exercise should wait. APOS will still provide a non-exercise physiological input.",
      conditioning,
      conditioningLabel: conditioningLabel(conditioning),
    };
  }
  if (readiness === "modify") {
    return {
      level: "modify",
      title: "Use a modified physical input",
      reason:
        "The physiological target remains the same, but the delivery method must respect the reported limitation.",
      conditioning,
      conditioningLabel: conditioningLabel(conditioning),
    };
  }
  if (readiness === "light-moderate") {
    return {
      level: "moderate",
      title: "Keep the physical output light to moderate",
      reason:
        "The state still determines the physiological direction, while the readiness response limits the dose.",
      conditioning,
      conditioningLabel: conditioningLabel(conditioning),
    };
  }
  return {
    level: "ready",
    title: "Ready for the state-matched physical output",
    reason:
      "The person reports unrestricted movement. Conditioning determines the relative dose.",
    conditioning,
    conditioningLabel: conditioningLabel(conditioning),
  };
}

export function buildPreparation({ state, current = {}, outputClass }) {
  const highActivation = ["organize-high-activation", "organize-overwhelm"].includes(
    state.target,
  );
  const demanding = outputClass === "high-output";
  const hydration = current.hydration || "unsure";
  const nourishment = current.nourishment || "unsure";
  const steps = [];

  if (highActivation) {
    steps.push(
      "Take several slow, controlled breaths before beginning. This organizes the transition into deliberate action; it does not replace the main intervention.",
    );
  }

  if (["little", "some", "unsure"].includes(hydration)) {
    steps.push(
      "Drink water before beginning the physical output, using an amount appropriate to you and the planned activity.",
    );
  } else if (demanding) {
    steps.push("Drink water before the demanding physical output.");
  }

  if (["not-recent", "light", "unsure"].includes(nourishment)) {
    steps.push(
      demanding
        ? "If you have not eaten sufficiently, use familiar nourishment before demanding exercise and allow enough time to feel ready."
        : "Use familiar nourishment when your report indicates that available energy or recent intake is insufficient for the planned activity.",
    );
  }

  return {
    required: steps.length > 0,
    steps,
    summary: steps.join(" ") || "No additional preparation was indicated by the current report.",
  };
}

const relatedActivitySuggestion = (selectedKey) => {
  const type = ACTIVITY_PROGRESSION_REPOSITORY[selectedKey]?.activityType;
  if (type === "rhythmic-aerobic") {
    return "Choose a similar rhythmic activity such as walking, cycling, swimming, dancing, or another continuous movement you recognize.";
  }
  if (type === "technical-skill" || type === "combat") {
    return "Choose a similar familiar skill activity, throwing game, racquet activity, technical drill, or non-contact movement practice.";
  }
  if (type === "social-casual" || type === "recreational-play") {
    return "Choose a similar casual game, low-pressure recreation, or shared movement activity.";
  }
  if (type === "strength") {
    return "Choose another familiar resistance, carrying, bodyweight, or strength-based activity at an appropriate dose.";
  }
  return "Choose a similar familiar sport, game, skill, or recreational activity that feels easier to begin today.";
};

const selectedActivityIntervention = ({ schedule, state, duration, eligibility }) => {
  const selectedKey = firstSelectedActivity(schedule);
  if (!selectedKey) return null;
  const label = activityLabel(selectedKey);
  const repository = ACTIVITY_PROGRESSION_REPOSITORY[selectedKey];
  const intensity = eligibility.level === "moderate"
    ? "Light to moderate and familiar"
    : eligibility.level === "modify"
      ? "Light and modified"
      : "Moderate and familiar";
  const instruction = repository?.base?.instruction
    ? `${label}: ${repository.base.instruction}. Participate at ${intensity.toLowerCase()} for approximately ${duration} minutes.`
    : `Participate in ${label} at ${intensity.toLowerCase()} for approximately ${duration} minutes.`;
  return {
    activityKey: selectedKey,
    activityName: label,
    activityType: repository?.activityType || "selected-recreation",
    title: `Use ${label} as today’s physical outlet`,
    instruction,
    reasonSelected:
      "The person declined a conventional exercise output and previously selected this sport or recreational activity.",
    physiologicalTarget: state.target,
    intensity,
  };
};

export function selectIntervention({
  state,
  eligibility,
  current = {},
  schedule = {},
  fallbackLevel = "primary",
}) {
  let level = normalizeFallbackLevel(fallbackLevel);
  if (eligibility.level === "pause" && !["somatic", "minimum"].includes(level)) {
    level = "somatic";
  }

  const environment = normalizeEnvironment(current.environment);
  const baseOutputClass = targetOutputClass(state, eligibility);
  const outputClass = level === "moderate" && baseOutputClass === "high-output"
    ? "moderate-output"
    : baseOutputClass;
  const baseIntensity = relativeIntensity({
    outputClass,
    conditioning: eligibility.conditioning,
    eligibility,
  });
  const recoveryDuration = recoveryDurationFor(outputClass, current.minutes);
  const duration = durationFor({
    outputClass,
    minutesAvailable: current.minutes,
    conditioning: eligibility.conditioning,
    fallbackLevel: level,
    recoveryDuration,
  });

  if (level === "selected-activity") {
    const selected = selectedActivityIntervention({
      schedule,
      state,
      duration,
      eligibility,
    });
    if (selected) {
      return {
        ...selected,
        fallbackLevel: level,
        fallbackLabel: FALLBACK_LABELS[level],
        outputClass: "selected-activity",
        duration,
        recoveryDuration: recoveryDurationFor("selected-activity", current.minutes),
        environment,
      };
    }
    level = "similar-activity";
  }

  if (level === "similar-activity") {
    const selectedKey = firstSelectedActivity(schedule);
    return {
      fallbackLevel: level,
      fallbackLabel: FALLBACK_LABELS[level],
      outputClass: "similar-activity",
      activityKey: "similar-activity",
      activityName: "Similar sport, game, or skill",
      activityType: "recreation",
      title: "Use a similar sport, game, or skill",
      instruction: relatedActivitySuggestion(selectedKey),
      reasonSelected:
        "The person declined the selected activity, so APOS preserved the physiological target while lowering the participation barrier.",
      physiologicalTarget: state.target,
      intensity: eligibility.level === "moderate" ? "Light to moderate" : "Moderate and familiar",
      duration,
      recoveryDuration: recoveryDurationFor("similar-activity", current.minutes),
      environment,
    };
  }

  if (level === "somatic") {
    const option = SOMATIC_OPTIONS[state.target] || SOMATIC_OPTIONS["maintain-capacity"];
    return {
      fallbackLevel: level,
      fallbackLabel: FALLBACK_LABELS[level],
      outputClass: "somatic",
      activityKey: "somatic",
      activityName: option.title,
      activityType: "somatic",
      title: option.title,
      instruction: option.instruction,
      reasonSelected:
        "The person declined exercise and recreation, or the readiness response does not support exercise. APOS still provides a physiological input.",
      physiologicalTarget: state.target,
      intensity: "Very light",
      duration: Math.min(option.duration, duration || option.duration),
      recoveryDuration: 0,
      environment,
    };
  }

  if (level === "minimum") {
    return {
      fallbackLevel: level,
      fallbackLabel: FALLBACK_LABELS[level],
      outputClass: "minimum",
      activityKey: "minimum-input",
      activityName: "Minimum useful input",
      activityType: "minimum-input",
      title: "Use the smallest useful input",
      instruction: MINIMUM_INPUTS[state.target] || MINIMUM_INPUTS["maintain-capacity"],
      reasonSelected:
        "The person declined the larger options. APOS provides a small achievable action rather than ending without an output.",
      physiologicalTarget: state.target,
      intensity: "Minimal",
      duration: Math.max(1, duration),
      recoveryDuration: 0,
      environment,
    };
  }

  const output =
    ENVIRONMENT_OUTPUTS[environment]?.[outputClass] ||
    ENVIRONMENT_OUTPUTS.inside["moderate-output"];
  const highOutput = outputClass === "high-output";
  const moderateOutput = outputClass === "moderate-output";
  return {
    fallbackLevel: level,
    fallbackLabel: FALLBACK_LABELS[level],
    outputClass,
    activityKey: `${environment}-${outputClass}`,
    activityName: output.title,
    activityType: outputClass,
    title: level === "moderate" ? "Use familiar moderate output" : output.title,
    instruction: `${output.examples} Work at ${baseIntensity.toLowerCase()} for approximately ${duration} minutes. ${
      highOutput
        ? "The objective is purposeful output without loss of control, technique, or awareness."
        : moderateOutput
          ? "Keep the rhythm purposeful and controlled."
          : "Keep the input easy to begin and repeat."
    }`,
    reasonSelected:
      level === "moderate"
        ? "The person declined high output, so APOS preserved the physiological target with a familiar moderate dose."
        : "This is the strongest available match between the directly reported state, readiness, conditioning, environment, and time.",
    physiologicalTarget: state.target,
    intensity: baseIntensity,
    duration,
    recoveryDuration,
    environment,
  };
}

export function buildCooldown(intervention) {
  if (["somatic", "minimum"].includes(intervention.outputClass)) {
    return {
      required: false,
      title: "Finish without adding demand",
      duration: 0,
      instruction:
        "End the brief input while it still feels manageable, then observe whether tension, urgency, energy, or attention changed.",
    };
  }

  const highOutput = intervention.outputClass === "high-output";
  const duration = Number.isFinite(Number(intervention.recoveryDuration))
    ? Number(intervention.recoveryDuration)
    : highOutput
      ? 8
      : 4;
  return {
    required: duration > 0,
    title: highOutput
      ? "Complete the parasympathetic recovery transition"
      : "Complete the recovery transition",
    duration,
    instruction: highOutput
      ? "Continue at a progressively easier pace until breathing, heart rate, muscular tension, and urgency begin moving toward recovery."
      : "Reduce the pace gradually and observe breathing, heart rate, muscular tension, energy, and emotional intensity.",
  };
}

const morningCategory = (state) => {
  if (["organize-high-activation", "organize-overwhelm"].includes(state.target)) {
    return "high-sympathetic";
  }
  if (["increase-activation", "restore-capacity"].includes(state.target)) {
    return "reduced-activation";
  }
  return "steady";
};

export function buildMorningRoutine({ state, schedule = {} }) {
  const slot = schedule.availability?.start;
  const available = timeFromAvailability(slot) || 5;
  return {
    title: "Morning preparation",
    time: schedule.times?.start || "08:00",
    duration: clamp(available, 3, 10),
    instruction: MORNING_ROUTINES[morningCategory(state)],
    changesPrimaryIntervention: false,
  };
}

export function buildBedtimeRoutine({ answers = {}, schedule = {} }) {
  const sleepScore = Number(answers.state?.sleep);
  const recoveryScore = Number(answers.state?.recovery);
  const difficultyReported =
    (Number.isFinite(sleepScore) && sleepScore <= 3) ||
    (Number.isFinite(recoveryScore) && recoveryScore <= 3) ||
    ["evening", "bedtime"].includes(schedule.recoveryWindow) ||
    schedule.sleepProtocol === true;
  const availability =
    schedule.bedtimeAvailability || schedule.availability?.bedtime || "5-10";
  const available = timeFromAvailability(availability) || 7;

  return {
    enabled: difficultyReported,
    title: difficultyReported ? "Evening and bedtime recovery" : "Bedtime maintenance",
    time: schedule.bedtime || schedule.times?.bedtime || "22:00",
    duration: clamp(available, 3, 20),
    eveningWalk: difficultyReported
      ? "After the final meal, use an easy intentional walk when practical. Treat it as recovery, not another training session."
      : null,
    instruction: difficultyReported
      ? BEDTIME_ROUTINES.default
      : BEDTIME_ROUTINES.limited,
    changesPrimaryIntervention: false,
  };
}

const recommendationRecord = (intervention) => ({
  activityName: intervention.activityName,
  activityKey: intervention.activityKey,
  activityType: intervention.activityType,
  versionOrExercise: intervention.instruction,
  intensity: intervention.intensity,
  duration: intervention.duration,
  recommendationRole: intervention.physiologicalTarget,
  reasonSelected: intervention.reasonSelected,
  scheduledOrOptional: "optional",
  day: null,
  startTime: null,
  environment: intervention.environment,
  socialFormat: "matched to preference",
  warmup: "Use the preparation sequence before demanding output.",
  cooldown: "Complete the matched recovery transition.",
  hydrationPrompt: "Hydrate before demanding output when indicated.",
  nourishmentPrompt: "Use familiar nourishment before demanding output when indicated.",
  recoveryPrompt: "Observe the post-intervention response.",
  exclusionReason: null,
  outputClass: intervention.outputClass,
  displayText: `${intervention.activityName} — ${intervention.intensity}, ${intervention.duration} minutes`,
});

const scheduledForDay = (schedule, dayCode) =>
  normalizeScheduledActivities(schedule).filter((activity) =>
    (activity.days || []).includes(dayCode),
  );

const scheduledSession = ({ activity, dateText, dayCode, schedule, index, demandingDay }) => {
  const label = activityLabel(activity.activity, "Scheduled activity");
  const duration = Math.max(10, Number(activity.duration) || 60);
  const intensity = activity.effort || "moderate";
  const preparation = demandingDay
    ? "Begin hydration and familiar nourishment before the first session, replenish between sessions, and complete the activity-specific warm-up."
    : "Use the state-matched preparation and complete the activity-specific warm-up.";
  const recovery = demandingDay
    ? "Complete the activity-specific cooldown, rehydrate, use familiar recovery nourishment, and preserve capacity between or after sessions."
    : "Complete the activity-specific cooldown, then continue easy movement until breathing, heart rate, and muscular tension begin moving toward recovery.";
  return {
    id: `${dateText}-scheduled-${activity.id || index + 1}`,
    date: dateText,
    day: DAY_LABELS[dayCode],
    period: "Scheduled activity",
    calendarGroup: `scheduled-${dateText}-${index + 1}`,
    time: activity.time || schedule.times?.end || "18:00",
    durationMinutes: duration,
    activity: label,
    activityOptions: [],
    recommendationOptions: [],
    progressionStage: "scheduled",
    progressionRole: "Scheduled activity sets the main physical demand",
    choiceInstruction: null,
    intensity: `${intensity} — relative to the person’s role and ability`,
    preparation,
    recovery,
    alternative:
      "If the scheduled activity is not appropriate today, use the next approved fallback while preserving the physiological target.",
  };
};

const buildWeek = ({
  state,
  eligibility,
  preparation,
  intervention,
  cooldown,
  morning,
  bedtime,
  schedule,
  now,
}) => {
  const recommendations = ["moderate", "selected-activity", "similar-activity", "somatic", "minimum"]
    .map((fallbackLevel) =>
      selectIntervention({
        state,
        eligibility,
        current: {
          ...schedule.current,
          environment: schedule.current?.environment || "inside",
          minutes: Number(schedule.current?.minutes) || 20,
        },
        schedule,
        fallbackLevel,
      }),
    )
    .filter(
      (option, index, all) =>
        option.activityKey !== intervention.activityKey &&
        all.findIndex((candidate) => candidate.activityKey === option.activityKey) === index,
    )
    .map(recommendationRecord);

  const sessions = [];
  for (let offset = 1; offset <= 7; offset += 1) {
    const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
    const dateText = formatDate(date);
    const dayCode = DAY_CODES[date.getDay()];
    sessions.push({
      id: `${dateText}-morning`,
      date: dateText,
      day: DAY_LABELS[dayCode],
      period: "Morning preparation",
      calendarGroup: `morning-${dateText}`,
      time: morning.time,
      durationMinutes: morning.duration,
      activity: morning.instruction,
      activityOptions: [],
      recommendationOptions: [],
      progressionStage: "morning-routine",
      progressionRole: "Separate preparation routine",
      choiceInstruction: null,
      intensity: "Very light",
      preparation: null,
      recovery: "This routine supports the day and does not replace the main intervention.",
      alternative: "Use comfortable breathing or gentle seated mobility.",
    });

    const scheduled = scheduledForDay(schedule, dayCode);
    if (scheduled.length) {
      const totalMinutes = scheduled.reduce(
        (sum, activity) => sum + Math.max(10, Number(activity.duration) || 60),
        0,
      );
      const demandingDay =
        totalMinutes > 90 ||
        scheduled.some(
          (activity) =>
            activity.effort === "high" && Math.max(10, Number(activity.duration) || 60) >= 60,
        );
      scheduled.forEach((activity, index) => {
        sessions.push(
          scheduledSession({
            activity,
            dateText,
            dayCode,
            schedule,
            index,
            demandingDay,
          }),
        );
      });
    } else {
      const primaryRecord = recommendationRecord(intervention);
      sessions.push({
        id: `${dateText}-main`,
        date: dateText,
        day: DAY_LABELS[dayCode],
        period: "Main physiological input",
        calendarGroup: `main-${dateText}`,
        time: schedule.times?.[mainTimeSlot(schedule)] || "18:30",
        durationMinutes: intervention.duration + cooldown.duration,
        activity: intervention.title,
        activityOptions: [primaryRecord.displayText],
        recommendationOptions: [primaryRecord, ...recommendations.slice(0, 2)],
        progressionStage: "state-matched",
        progressionRole: "State-matched intervention",
        choiceInstruction: "Use the strongest acceptable option.",
        intensity: intervention.intensity,
        preparation: preparation.summary,
        recovery: cooldown.instruction,
        alternative: recommendations[0]?.displayText || "Use the somatic fallback.",
      });
    }

    sessions.push({
      id: `${dateText}-bedtime`,
      date: dateText,
      day: DAY_LABELS[dayCode],
      period: "Bedtime support",
      calendarGroup: `bedtime-${dateText}`,
      time: bedtime.time,
      durationMinutes: bedtime.duration,
      activity: [bedtime.eveningWalk, bedtime.instruction].filter(Boolean).join(" "),
      activityOptions: [],
      recommendationOptions: [],
      progressionStage: "bedtime-routine",
      progressionRole: "Separate sleep and recovery protocol",
      choiceInstruction: null,
      intensity: "Very light",
      preparation: null,
      recovery: "This routine is generated from sleep and recovery responses and does not change the main intervention.",
      alternative: "Use two to five minutes of lower-stimulation breathing or gentle mobility.",
    });
  }

  const dailyPlan = sessions.reduce((days, session) => {
    const existing = days.find((day) => day.date === session.date);
    if (existing) existing.sessions.push(session);
    else days.push({ date: session.date, day: session.day, sessions: [session] });
    return days;
  }, []);

  return {
    title: `${state.system}: ${state.targetExplanation}`,
    target: state.targetExplanation,
    input: intervention.activityName,
    purpose: state.target,
    progressionRule:
      "Preserve the physiological target. If the strongest option is declined, move through moderate output, selected recreation, similar activity, somatic work, and a minimum useful input.",
    intensity: intervention.intensity,
    recovery: cooldown.instruction,
    alternative: recommendations[0]?.displayText || "Use a somatic option.",
    recommendations,
    frequency: sessions.length,
    days: 7,
    sessions,
    dailyPlan,
    experiment:
      "Morning and bedtime routines are generated separately. They support preparation and sleep but do not overwrite the state-matched main intervention.",
  };
};

export function buildAposPlan({
  profile,
  mapping,
  answers = {},
  now = new Date(),
  currentOverride = {},
  fallbackLevel = "primary",
}) {
  const current = {
    ...(answers.current || {}),
    ...currentOverride,
  };
  const schedule = {
    ...(answers.schedule || {}),
    current,
    scheduledActivities: normalizeScheduledActivities(answers.schedule || {}),
    availability: {
      ...(answers.schedule?.availability || {}),
    },
    times: {
      ...(answers.schedule?.times || {}),
    },
  };
  const state = interpretReportedState(current);
  const eligibility = determineExerciseEligibility({ answers, current });
  const intervention = selectIntervention({
    state,
    eligibility,
    current,
    schedule,
    fallbackLevel,
  });
  const preparation = buildPreparation({
    state,
    current,
    outputClass: intervention.outputClass,
  });
  const cooldown = buildCooldown(intervention);
  const morning = buildMorningRoutine({ state, schedule });
  const bedtime = buildBedtimeRoutine({ answers, schedule });
  const week = buildWeek({
    state,
    eligibility,
    preparation,
    intervention,
    cooldown,
    morning,
    bedtime,
    schedule,
    now,
  });

  return {
    profileKey: profile?.key || "regulation-building",
    reportedState: state,
    currentState: {
      labels: state.labels,
      intensity: state.intensity,
      energy: state.energy,
      system: state.system,
      target: state.target,
      summary: `${state.labels.join(" + ")} · intensity ${state.intensity}/5 · energy ${state.energy}/5`,
    },
    stateInterpretation: {
      system: state.system,
      physiologicalTarget: state.target,
      participantExplanation: state.participantBiology,
      clinicianExplanation: state.clinicianBiology,
      targetExplanation: state.targetExplanation,
    },
    readiness: eligibility,
    exerciseEligibility: eligibility,
    preparation,
    immediate: {
      ...intervention,
      title: intervention.title,
      durationMinutes: intervention.duration,
      duration: `${intervention.duration} minutes · ${intervention.intensity}`,
      instruction: intervention.instruction,
      recovery: cooldown.instruction,
      rationale: `${state.participantBiology} ${state.targetExplanation} ${intervention.reasonSelected}`,
      adjustment:
        "Declining an option does not end the process. Move to the next approved fallback while preserving the physiological target.",
      reassess:
        "Afterward, record whether tension, urgency, energy, attention, heart rate, or the behavioural urge changed.",
    },
    cooldown,
    routines: {
      morning,
      bedtime,
    },
    anchor: {
      slot: mainTimeSlot(schedule),
      slotLabel: "Main physiological input",
      time: schedule.times?.[mainTimeSlot(schedule)] || "18:30",
      durationMinutes: intervention.duration,
      stressWindow: schedule.stressWindow,
      activity: intervention.activityName,
      socialFormat: schedule.socialPreference || "either",
      rationale:
        "The main intervention is placed near the reported pressure window when practical. Morning and bedtime routines remain separate.",
    },
    loops: {
      dependencySupported: Boolean((answers.behaviors || []).length || current.urge === "seek-relief"),
      dependencyNode: current.urge === "seek-relief" ? "outsourced-relief" : null,
      dependencyLabel:
        current.urge === "seek-relief" ? "Outsourced Relief" : "Educational comparison",
      evidence: current.urge === "seek-relief"
        ? "The current report includes an urge for familiar relief or stimulation."
        : "The Dependency Loop is used as an educational comparison unless a repeated relief pattern is reported.",
      interruptionPoint:
        "Between the reported nervous-system response and the next automatic behaviour.",
      adaptiveNode: "intervention",
      adaptiveLabel: "Matched Adaptive Intervention",
      route:
        "Reported state → physiological target → preparation → matched output → cooldown → parasympathetic recovery → observed response",
      rationale:
        "APOS provides the reported state with an appropriate physiological output, then makes recovery part of the intervention.",
    },
    bottomLine: {
      happening:
        `${profile?.body || "The profile provides the baseline context."} The current state is directly self-reported and is used to select today’s physiological direction.`,
      shameRelease:
        "The reported behaviour is treated as information about the current adaptive pattern, not as a character judgement.",
      whyPlanHelps:
        `${state.targetExplanation} Preparation, cooldown, morning support, and bedtime support are kept separate so each variable performs the correct job.`,
      overallSuggestion:
        "Use the strongest acceptable output. If it is declined, move to a familiar moderate output, selected recreation, a similar activity, somatic work, or the minimum useful input.",
      consultation:
        "Adults remain responsible for choosing options appropriate to their body, readiness, environment, and professional guidance.",
    },
    week,
    mappingInput: mapping?.recommendedInput?.label || null,
  };
}

export function rematchAposPlan({ planInput, fallbackLevel }) {
  return buildAposPlan({
    ...planInput,
    fallbackLevel: normalizeFallbackLevel(fallbackLevel),
  });
}

export function nextFallbackLevel(currentLevel) {
  const index = FALLBACK_LEVELS.indexOf(normalizeFallbackLevel(currentLevel));
  return FALLBACK_LEVELS[Math.min(index + 1, FALLBACK_LEVELS.length - 1)];
}
